import React, { FC, useEffect } from 'react'

import { styled } from '@mui/system'
import { TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import moment, { Moment } from 'moment'

import { FIELD_STATEMENT_OPERATORS } from '../../../context/CustomField/CustomFieldTypes'
import { ValueError } from '../../../store/groupConditionsSlice'
import { useMultipleLocalizedText } from '../../../context/Localization/localizationHooks'
import { useViewModeReadOnly } from '../../../context/ViewMode/viewModeHooks'

const BetweenContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    height: '40px',
}))
const SpanOverride = styled('span')(() => ({
    color: '#263238',
    fontWeight: 600,
    fontSize: 16,
    fontFamily: 'Open Sans',
    margin: '0px 8px',
}))
export interface DateStatementProps {
    operator: typeof FIELD_STATEMENT_OPERATORS['Date'][number]['key']
    values: Array<string>
    valueError: ValueError
    onChangeValue: (value: Array<string>) => void
}
const DateStatement: FC<DateStatementProps> = ({ operator, values, onChangeValue, valueError }) => {
    const momentFormat = 'MM/DD/yyyy'

    const {
        ON,
        FROM,
        TO,
    } = useMultipleLocalizedText()

    const isReadOnly = useViewModeReadOnly()

    const handleDateChange = (index: number) => (newValue: Moment | null) => {
        const dates = [...values]

        if (index === 0 && operator === 'CUSTOM_RANGE') {
            dates[1] = ''
        }

        dates[index] = moment(newValue, momentFormat, true).format(momentFormat)

        if (dates[index] === 'Invalid date') {
            dates[index] = ''
        }

        onChangeValue(dates)
    }

    const handleKeyDown = (e: any) => {
        e.preventDefault()
    }

    // TODO: this should probably move into redux state at some point
    useEffect(() => {
        if (operator === 'CUSTOM_RANGE') {
            onChangeValue([values[0] || '', ''])
        } else {
            onChangeValue([values[0] || ''])
        }
    }, [operator])

    const dateOneError = valueError && valueError.errors.findIndex(error => error.valueIndex === 0 && error.message) !== -1
    const dateTwoError = valueError && valueError.errors.findIndex(error => error.valueIndex === 1 && error.message) !== -1

    return (
        <>
            {
                (operator === 'SPECIFIC_DATE' || operator === 'AFTER' || operator === 'BEFORE') && (
                    <BetweenContainer>
                        { operator === 'SPECIFIC_DATE' && <SpanOverride>{ON}</SpanOverride> }
                        <DatePicker
                            inputFormat={momentFormat}
                            value={moment(values[0], momentFormat).isValid() ? moment(values[0], momentFormat, true) : null}
                            onChange={handleDateChange(0)}
                            renderInput={(params: any) => <TextField {...params} size="small" onKeyDown={handleKeyDown} />}
                            disabled={isReadOnly}
                            InputProps={{ readOnly: true, error: dateOneError }}
                        />
                    </BetweenContainer>
                )
            }
            {
                operator === 'CUSTOM_RANGE' && (
                    <BetweenContainer>
                        <SpanOverride>{FROM}</SpanOverride>
                        <DatePicker
                            inputFormat={momentFormat}
                            value={moment(values[0], momentFormat, true).isValid() ? moment(values[0], momentFormat, true) : null}
                            onChange={handleDateChange(0)}
                            renderInput={(params: any) => <TextField {...params} size="small" onKeyDown={handleKeyDown} />}
                            disabled={isReadOnly}
                            InputProps={{ readOnly: true, error: dateOneError }}
                        />
                        <SpanOverride>{TO}</SpanOverride>
                        <DatePicker
                            inputFormat={momentFormat}
                            value={moment(values[1], momentFormat, true).isValid() ? moment(values[1], momentFormat, true) : null}
                            onChange={handleDateChange(1)}
                            renderInput={(params: any) => <TextField {...params} size="small" onKeyDown={handleKeyDown} />}
                            disabled={!values[0] || !moment(values[0], momentFormat).isValid() || isReadOnly}
                            minDate={moment(values[0], momentFormat, true).add(1, 'day')}
                            InputProps={{ readOnly: true, error: dateTwoError }}
                        />
                    </BetweenContainer>
                )
            }
        </>
    )
}

export default DateStatement